import { useState } from "react";
import { useRouter } from "@/lib/router";
import { createClient } from "@/lib/supabase/client";
import { authErrorMessage } from "@/lib/auth/messages";
import { passwordResetGate } from "@/lib/auth/redirect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { checkPasswordRules, isPasswordValid, MIN_PASSWORD_LENGTH, PASSWORD_RULES } from "@/lib/auth/password-rules";
import { Check, X } from "lucide-react";

/** 재설정 메일 링크(/auth/confirm)로 세션이 만들어진 뒤 새 비밀번호를 정하는 폼. */
export function UpdatePasswordForm() {
  const router = useRouter();
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!isPasswordValid(password)) {
      setError("비밀번호 조건을 모두 만족해주세요.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("비밀번호가 서로 달라요.");
      return;
    }

    setIsLoading(true);
    const supabase = createClient();
    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      setError(
        error.code === "same_password"
          ? "이전과 다른 비밀번호를 입력해주세요."
          : authErrorMessage(error),
      );
      setIsLoading(false);
      return;
    }

    // 비밀번호가 유출돼서 바꾸는 경우를 위해 다른 기기의 로그인은 끊는다.
    await supabase.auth.signOut({ scope: "others" });
    passwordResetGate.close();
    router.push("/");
    router.refresh();
  }

  return (
    <Card className="w-full max-w-sm border-0 bg-transparent shadow-none ring-0">
      <CardHeader className="items-center text-center">
        <CardTitle className="text-2xl">새 비밀번호 설정</CardTitle>
        <CardDescription>앞으로 사용할 비밀번호를 입력하세요</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="password">새 비밀번호</Label>
            <Input
              className="h-11"
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {password.length > 0 ? (
              <ul className="flex flex-col gap-1 text-xs">
                {checkPasswordRules(password).map((rule) => (
                  <li
                    key={rule.label}
                    className={
                      rule.passed
                        ? "flex items-center gap-1.5 text-emerald-600 dark:text-emerald-500"
                        : "flex items-center gap-1.5 text-muted-foreground"
                    }
                  >
                    {rule.passed ? <Check className="size-3.5" /> : <X className="size-3.5" />}
                    {rule.label}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">
                {PASSWORD_RULES.map((r) => r.label).join(" · ")}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="password-confirm">새 비밀번호 확인</Label>
            <Input
              className="h-11"
              id="password-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
            />
          </div>
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
          <Button type="submit" disabled={isLoading} className="h-11 w-full rounded-full">
            {isLoading ? "변경 중..." : "비밀번호 변경"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
